'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { LinkProps } from 'next/link';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { PLCTA } from './atoms';
import { PERFORMANCE_LAB_HERO_VIDEO } from './media';
import { useOpenCallbackModal } from './hooks';

import {
  DiagnosticsSignalMarquee,
  type SignalMarqueeItem,
} from './DiagnosticsSignalMarquee';
import styles from './HeroSection.module.css';

const DESKTOP_MIN_WIDTH = 768;

/** i18n prefix for the diagnostics hero marquee pills (`${prefix}.${key}.label`). */
const DIAGNOSTICS_MARQUEE_PREFIX = 'diagnosticsMarquee';

/** Diagnostics signals shown in the main hero's signal marquee. */
const DIAGNOSTICS_MARQUEE_SIGNALS: ReadonlyArray<SignalMarqueeItem> = [
  { key: 'genetics', default: 'Genetics' },
  { key: 'gutMicrobiome', default: 'Gut Microbiome' },
  { key: 'dexa', default: 'DEXA' },
  { key: 'bloodVision', default: 'Blood Vision' },
  { key: 'vo2Max', default: 'VO2 Max' },
  { key: 'boneDensity', default: 'Bone Density' },
  { key: 'biomechanics', default: 'Biomechanics' },
];

const HERO_POSTER_IMAGE = {
  desktop: 'web_v2/performance-lab/landing-page/hero/desktop.png',
  mobile: 'web_v2/performance-lab/landing-page/hero/mobile.png',
};

const HERO_STATS = [
  {
    valueKey: 'hero.stats.signals.value',
    valueDefault: 'UP TO 3500',
    labelKey: 'hero.stats.signals.label',
    labelDefault: 'Signals',
  },
  {
    valueKey: 'hero.stats.duration.value',
    valueDefault: '~3 HOURS',
    labelKey: 'hero.stats.duration.label',
    labelDefault: 'Start to finish',
  },
  {
    valueKey: 'hero.stats.direction.value',
    valueDefault: 'ONE YEAR',
    labelKey: 'hero.stats.direction.label',
    labelDefault: 'Of Direction',
  },
] as const;

type HeroVideoSources = {
  desktop: string;
  mobile: string;
  mobileFallback?: string;
};

type HeroPosterSources = {
  desktop: string;
  mobile: string;
};

type HeroBackground =
  | { type: 'video' }
  | { type: 'video'; sources: HeroVideoSources; poster: HeroPosterSources }
  | { type: 'image'; desktop: string; mobile: string };

type HeroCtaConfig = {
  label: string;
  ariaLabel?: string;
  href?: LinkProps['href'];
  onClick?: () => void;
  buttontype?: string;
};

export type HeroSectionProps = {
  ariaLabel?: string;
  /** Overline above the title. Pass `null` to hide it. */
  locationLabel?: string | null;
  title?: string;
  subtitle?: string;
  /** Subtitle text color. Defaults to white. */
  subtitleColor?: string;
  /** Desktop copy alignment. Mobile is always centered. Defaults to `left`. */
  align?: 'left' | 'center';
  /** Background media. Defaults to the landing hero video. */
  background?: HeroBackground;
  /** Overrides the default CTA. When omitted, the landing explore-plans CTA is used. */
  cta?: HeroCtaConfig;
  /** Hide the hero CTA entirely (overrides `cta` and the default). */
  hideCta?: boolean;
  /** Whether to render the desktop stats row. Defaults to `true`. */
  showStats?: boolean;
  /** Dark scrim over the hero media. Defaults to `true`. */
  showOverlay?: boolean;
  /** Scrim color when `showOverlay`. Defaults to `rgba(0, 0, 0, 0.56)`. */
  overlayColor?: string;
  /** Inset the media with rounded corners on the page background. Defaults to `true`. */
  framed?: boolean;
  /** Decorative image (CDN path / URL) layered over the media behind the copy. */
  decoration?: string;
  marqueeSignals?: ReadonlyArray<SignalMarqueeItem>;
  marqueeLabelPrefix?: string;
  marqueeAriaLabel?: string;
  /** Mobile hero title font size in px. Defaults to 64. */
  titleMobileFontSize?: number;
};

export const HeroSection: React.FC<HeroSectionProps> = ({
  ariaLabel,
  locationLabel,
  title,
  subtitle,
  subtitleColor,
  align = 'left',
  background = { type: 'video' },
  cta,
  hideCta = false,
  showStats = true,
  showOverlay = true,
  overlayColor = 'rgba(0, 0, 0, 0.56)',
  framed = true,
  decoration,
  marqueeSignals = DIAGNOSTICS_MARQUEE_SIGNALS,
  marqueeLabelPrefix = DIAGNOSTICS_MARQUEE_PREFIX,
  marqueeAriaLabel,
  titleMobileFontSize,
}) => {
  const t = useTranslations('performance-lab');
  const handleBookCallback = useOpenCallbackModal();
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const { width } = useWindowSize();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isDesktop = width >= DESKTOP_MIN_WIDTH;
  const isImageBg = background.type === 'image';

  const resolvedLocation =
    locationLabel === undefined
      ? t('hero.locationLabel')
      : locationLabel;
  const resolvedTitle = title ?? t('hero.titleFinal');
  const resolvedSubtitle =
    subtitle ??
    t('hero.subtitle');

  const resolvedHeroVideoSrc = useMemo(() => {
    if (background.type === 'video' && 'sources' in background) {
      return isDesktop ? background.sources.desktop : background.sources.mobile;
    }
    return isDesktop
      ? PERFORMANCE_LAB_HERO_VIDEO.desktop
      : PERFORMANCE_LAB_HERO_VIDEO.mobile;
  }, [isDesktop, background]);

  const resolvedHeroVideoFallbackSrc = useMemo(() => {
    if (background.type === 'video' && 'sources' in background) {
      return isDesktop ? undefined : background.sources.mobileFallback;
    }
    return isDesktop ? undefined : PERFORMANCE_LAB_HERO_VIDEO.mobileFallback;
  }, [isDesktop, background]);

  const resolvedHeroPosterSrc = useMemo(() => {
    if (background.type === 'image') {
      return isDesktop ? background.desktop : background.mobile;
    }
    if (background.type === 'video' && 'poster' in background) {
      return isDesktop ? background.poster.desktop : background.poster.mobile;
    }
    return isDesktop ? HERO_POSTER_IMAGE.desktop : HERO_POSTER_IMAGE.mobile;
  }, [isDesktop, background]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [resolvedHeroVideoSrc, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const video = heroVideoRef.current;
    if (!video) return;

    const kickPlayback = () => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.volume = 0;
      void video
        .play()
        .then(() => setIsVideoPlaying(true))
        .catch(() => {
          /* autoplay blocked */
        });
    };

    video.load();
    kickPlayback();

    const mediaEvents = [
      'loadeddata',
      'canplay',
      'canplaythrough',
      'loadedmetadata',
    ] as const;
    mediaEvents.forEach((ev) => video.addEventListener(ev, kickPlayback));
    window.addEventListener('pageshow', kickPlayback);

    return () => {
      mediaEvents.forEach((ev) => video.removeEventListener(ev, kickPlayback));
      window.removeEventListener('pageshow', kickPlayback);
    };
  }, [resolvedHeroVideoSrc, prefersReducedMotion]);

  const handleHeroVideoError = useCallback(() => {
    const video = heroVideoRef.current;
    if (!video || !resolvedHeroVideoFallbackSrc) return;
    if (video.src === resolvedHeroVideoFallbackSrc) return;
    video.src = resolvedHeroVideoFallbackSrc;
    video.load();
  }, [resolvedHeroVideoFallbackSrc]);

  const showPosterOnly = isImageBg || prefersReducedMotion || !isVideoPlaying;

  return (
    <section
      className={styles.section}
      data-pl-hero
      aria-label={ariaLabel ?? t('hero.aria.hero')}
    >
      <div className={styles.mediaFrame} data-framed={framed ? 'true' : 'false'}>
        <div
          className={styles.heroMediaArea}
          data-framed={framed ? 'true' : 'false'}
        >
          <div className={styles.background} aria-hidden="true">
            {!isImageBg && !prefersReducedMotion && (
              <video
                className={styles.heroVideo}
                key={resolvedHeroVideoSrc}
                ref={heroVideoRef}
                src={resolvedHeroVideoSrc}
                autoPlay
                loop
                muted
                playsInline
                controls={false}
                controlsList="nodownload noplaybackrate nofullscreen"
                disablePictureInPicture
                disableRemotePlayback
                aria-hidden="true"
                preload="auto"
                onPlaying={() => setIsVideoPlaying(true)}
                onError={handleHeroVideoError}
                {...{ 'webkit-playsinline': 'true' }}
              />
            )}
            {resolvedHeroPosterSrc && (
              <CustomImage
                className={styles.heroPoster}
                data-hidden={
                  !showPosterOnly && isVideoPlaying ? 'true' : undefined
                }
                src={resolvedHeroPosterSrc}
                alt=""
                fill
                sizes="100vw"
                aria-hidden="true"
                priority
              />
            )}
            {showOverlay ? (
              <div
                className={styles.overlay}
                style={{ '--hs-overlay-color': overlayColor } as CSSProperties}
              />
            ) : null}
          </div>

          {decoration ? (
            <img
              className={styles.decoration}
              src={decoration}
              alt=""
              aria-hidden="true"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}

          <div
            className={styles.copyBlock}
            data-align={align}
            data-show-stats={showStats ? 'true' : 'false'}
          >
            {resolvedLocation ? (
              <p className={styles.location}>{resolvedLocation}</p>
            ) : null}
            <div
              className={styles.titleWrap}
              style={
                titleMobileFontSize
                  ? ({
                      '--hs-title-mobile-size': `${titleMobileFontSize}px`,
                    } as CSSProperties)
                  : undefined
              }
            >
              <h1 className={`${styles.title} pl-dharma-title`}>
                {resolvedTitle}
              </h1>
            </div>
            <p
              className={styles.subtitle}
              style={
                subtitleColor
                  ? ({ '--hs-subtitle-color': subtitleColor } as CSSProperties)
                  : undefined
              }
            >
              {resolvedSubtitle}
            </p>
            {!hideCta &&
              (cta ? (
                cta.onClick ? (
                  <PLCTA
                    className={styles.heroCta}
                    $plVariant="white"
                    type="button"
                    onClick={cta.onClick}
                    aria-label={cta.ariaLabel}
                    data-buttontype={
                      cta.buttontype ?? 'performance_lab_hero_cta'
                    }
                  >
                    {cta.label}
                  </PLCTA>
                ) : (
                  <PLCTA
                    className={styles.heroCta}
                    $plVariant="white"
                    href={cta.href}
                    aria-label={cta.ariaLabel}
                    data-buttontype={
                      cta.buttontype ?? 'performance_lab_hero_cta'
                    }
                  >
                    {cta.label}
                  </PLCTA>
                )
              ) : (
                <PLCTA
                  className={styles.heroCta}
                  $plVariant="white"
                  type="button"
                  onClick={handleBookCallback}
                  aria-label={t('hero.ctaBookCallbackAria')}
                  data-buttontype="performance_lab_hero_book_callback"
                >
                  {t('hero.ctaBookCallback')}
                </PLCTA>
              ))}
          </div>

          {showStats && (
            <dl className={styles.statsRow} aria-label={t('hero.stats.aria')}>
              {HERO_STATS.map((stat) => (
                <div className={styles.statItem} key={stat.valueKey}>
                  <div className={styles.statGroup}>
                    <dt className={`${styles.statValue} pl-dharma-title`}>
                      {t.has(stat.valueKey)
                        ? t(stat.valueKey)
                        : stat.valueDefault}
                    </dt>
                    <dd className={styles.statLabel}>
                      {t.has(stat.labelKey)
                        ? t(stat.labelKey)
                        : stat.labelDefault}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <div className={styles.heroSignalBand}>
        <DiagnosticsSignalMarquee
          signals={marqueeSignals}
          labelPrefix={marqueeLabelPrefix}
          ariaLabel={marqueeAriaLabel}
        />
      </div>
    </section>
  );
};
